import { Close } from "@mui/icons-material";
import {
    FormControl,
    IconButton,
    InputLabel,
    Menu,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    TextField,
} from "@mui/material";
import { GridFilterListIcon, GridFilterModel } from "@mui/x-data-grid-pro";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useDebouncedEffect } from "@react-hookz/web";
import { format, parseISO } from "date-fns";
import { useEffect, useRef, useState } from "react";

interface IProps {
    /**
     * Callback function to handle filter model changes.
     * @param filterModel The filter model to apply.
     */
    handleFilterModelChange: (filterModel: GridFilterModel) => void;
}

/**
 * Displays the filter for the clause checklists list.
 */
export const ClauseChecklistsFilter = ({ handleFilterModelChange }: IProps) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const inputRef = useRef<HTMLInputElement>(null);
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
        setSelectedColumn("name");
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const [selectedColumn, setSelectedColumn] = useState<string>("name");
    const [filterValue, setFilterValue] = useState<string>("");

    // Triggers filter change when column or value changes
    useDebouncedEffect(
        () => {
            if (selectedColumn) {
                const filterModel: GridFilterModel = {
                    items: [
                        {
                            field: selectedColumn,
                            operator:
                                selectedColumn === "updated_at"
                                    ? "equals"
                                    : "contains",
                            value: filterValue,
                        },
                    ],
                };
                handleFilterModelChange(filterModel);
            } else {
                // Clear filter if no column is selected
                handleFilterModelChange({ items: [] });
            }
        },
        [selectedColumn, filterValue, handleFilterModelChange],
        250
    );

    const handleColumnChange = (event: SelectChangeEvent<string>) => {
        setSelectedColumn(event.target.value);
    };

    const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFilterValue(event.target.value);
    };

    const handleDateChange = (newValue: Date | null) => {
        if (!newValue) {
            setFilterValue("");
        } else {
            const formattedValue = format(new Date(newValue), "yyyy-MM-dd");
            setFilterValue(formattedValue);
        }
    };

    const handleClear = () => {
        setSelectedColumn("");
        setFilterValue("");
        handleClose();
    };

    // handles focusing the input when the column changes or the menu opens
    useEffect(() => {
        if (selectedColumn && inputRef.current) {
            const timer = setTimeout(() => {
                inputRef.current?.focus();
            }, 0);
            return () => clearTimeout(timer);
        }
    }, [selectedColumn]);

    // Handler for focusing after menu enters
    const handleMenuEntered = () => {
        inputRef.current?.focus();
    };

    return (
        <div>
            <IconButton onClick={handleClick}>
                <GridFilterListIcon fontSize="small" color="primary" />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    sx: { paddingTop: 0, paddingBottom: 0 },
                }}
                TransitionProps={{ onEntered: handleMenuEntered }}
                transitionDuration={0}
            >
                <Stack
                    direction="row"
                    alignItems={"center"}
                    px={1}
                    py={2}
                    gap={3}
                    sx={{ overflowX: "auto" }}
                >
                    <IconButton size="small" onClick={handleClear}>
                        <Close fontSize="small" />
                    </IconButton>

                    <FormControl size="small" sx={{ flexShrink: 0 }}>
                        <InputLabel id="columns-label">Columns</InputLabel>
                        <Select
                            labelId="columns-label"
                            label="Columns"
                            sx={{ width: 150 }}
                            value={selectedColumn}
                            onChange={handleColumnChange}
                        >
                            <MenuItem value={"name"}>Name</MenuItem>
                            <MenuItem value={"description"}>
                                Description
                            </MenuItem>
                            <MenuItem value={"updated_at"}>
                                Last Updated
                            </MenuItem>
                        </Select>
                    </FormControl>

                    {selectedColumn === "updated_at" ? (
                        <DatePicker
                            label="Date"
                            value={filterValue ? parseISO(filterValue) : null}
                            onChange={handleDateChange}
                            slotProps={{
                                textField: {
                                    size: "small",
                                    inputRef: inputRef,
                                },
                            }}
                            sx={{ width: 190, flexShrink: 0 }}
                            disabled={!selectedColumn}
                        />
                    ) : (
                        <TextField
                            label="Value"
                            size="small"
                            sx={{ width: 190, flexShrink: 0 }}
                            value={filterValue}
                            onChange={handleValueChange}
                            disabled={!selectedColumn}
                            inputRef={inputRef}
                        />
                    )}
                </Stack>
            </Menu>
        </div>
    );
};
